import React, { useCallback, useEffect, useState } from 'react';
import { Check, X, Star, Trash2, RefreshCw, ShieldAlert } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import StarRating from '@/components/testimonials/StarRating';
import {
  Testimonial,
  TestimonialStatus,
  deleteTestimonial,
  displayLocation,
  displayName,
  fetchAllTestimonials,
  setTestimonialFeatured,
  setTestimonialStatus,
} from '@/lib/testimonials';

/**
 * ADMIN MODERATION QUEUE.
 *
 * Approve / reject / feature / delete. Every write goes through the shared
 * helpers in src/lib/testimonials.ts and is enforced again by RLS — a
 * non-admin who reaches this component sees the list fail, not succeed.
 */
const TABS: { key: TestimonialStatus | 'all'; labelKey: string }[] = [
  { key: 'pending', labelKey: 'testi.adminPending' },
  { key: 'approved', labelKey: 'testi.adminApproved' },
  { key: 'rejected', labelKey: 'testi.adminRejected' },
  { key: 'all', labelKey: 'testi.adminAll' },
];

const AdminTestimonials: React.FC = () => {
  const { t } = useI18n();
  const [tab, setTab] = useState<TestimonialStatus | 'all'>('pending');
  const [rows, setRows] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    const data = await fetchAllTestimonials(tab);
    setRows(data);
    setLoading(false);
  }, [tab]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (id: string, fn: () => Promise<{ error?: string }>) => {
    setBusy(id);
    await fn();
    setBusy('');
    void load();
  };

  const btn =
    'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold transition-colors disabled:opacity-50';

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-slate-900">{t('testi.adminTitle')}</h2>
          <p className="mt-1 text-sm text-slate-500">{t('testi.adminSub')}</p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> {t('sec.refresh')}
        </button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            type="button"
            onClick={() => setTab(tb.key)}
            aria-pressed={tab === tb.key}
            className={`rounded-full border px-4 py-1.5 text-xs font-bold transition-all ${
              tab === tb.key
                ? 'border-brand-navy bg-brand-navy text-white'
                : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
            }`}
          >
            {t(tb.labelKey)}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-slate-500">{t('common.loading')}</p>
      ) : !rows.length ? (
        <p className="mt-6 rounded-xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
          {t('testi.adminEmpty')}
        </p>
      ) : (
        <ul className="mt-5 space-y-4">
          {rows.map((r) => (
            <li key={r.id} className="rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <StarRating value={r.rating} size="sm" />
                  <span className="text-sm font-bold text-slate-900">
                    {displayName(r.name)}, {displayLocation(r.city, r.state)}
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold uppercase text-slate-600">
                    {r.customer_type === 'contractor'
                      ? t('testi.typeContractor')
                      : t('testi.typeHomeowner')}
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold uppercase text-slate-600">
                    {r.language}
                  </span>
                  {r.featured && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold uppercase text-amber-800">
                      <Star className="h-3 w-3 fill-amber-500 text-amber-500" aria-hidden="true" />
                      {t('testi.adminFeatured')}
                    </span>
                  )}
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${
                      r.status === 'approved'
                        ? 'bg-emerald-100 text-emerald-800'
                        : r.status === 'rejected'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-orange-100 text-orange-800'
                    }`}
                  >
                    {r.status}
                  </span>
                </div>
                <span className="text-xs text-slate-400">
                  {new Date(r.created_at).toLocaleDateString()}
                </span>
              </div>

              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-700">
                “{r.body}”
              </p>

              <p className="mt-2 text-xs text-slate-500">
                {r.recommend ? t('testi.yes') : t('testi.no')} · {t('testi.recommendQ')}
                {r.email ? ` · ${r.email}` : ''}
                {r.name !== displayName(r.name) ? ` · ${r.name}` : ''}
              </p>

              {!r.permission && (
                <p className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-red-50 px-2 py-1 text-xs font-semibold text-red-700">
                  <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('testi.adminNoPermission')}
                </p>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {r.status !== 'approved' && (
                  <button
                    type="button"
                    disabled={busy === r.id || !r.permission}
                    onClick={() => act(r.id, () => setTestimonialStatus(r.id, 'approved'))}
                    className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}
                  >
                    <Check className="h-3.5 w-3.5" aria-hidden="true" /> {t('testi.adminApprove')}
                  </button>
                )}
                {r.status !== 'rejected' && (
                  <button
                    type="button"
                    disabled={busy === r.id}
                    onClick={() => act(r.id, () => setTestimonialStatus(r.id, 'rejected'))}
                    className={`${btn} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" /> {t('testi.adminReject')}
                  </button>
                )}
                <button
                  type="button"
                  disabled={busy === r.id || !r.permission}
                  onClick={() => act(r.id, () => setTestimonialFeatured(r.id, !r.featured))}
                  className={`${btn} border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100`}
                >
                  <Star className="h-3.5 w-3.5" aria-hidden="true" />
                  {r.featured ? t('testi.adminUnfeature') : t('testi.adminFeature')}
                </button>
                <button
                  type="button"
                  disabled={busy === r.id}
                  onClick={() => {
                    if (window.confirm(t('testi.adminDeleteConfirm'))) {
                      void act(r.id, () => deleteTestimonial(r.id));
                    }
                  }}
                  className={`${btn} border border-red-200 bg-white text-red-700 hover:bg-red-50`}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> {t('testi.adminDelete')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default AdminTestimonials;
